// Default Client Environment Configuration
// IE Hub - Default API endpoints and environment settings

import { PUBLIC_ENV } from '@shared/env';

export interface APIConfig {
  baseURL: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
}

export interface AuthEndpoints {
  login: string;
  logout: string;
  refresh: string;
  profile: string;
}

export interface AuthConfig {
  tokenKey: string;
  refreshTokenKey: string;
  tokenExpiry: number;
  endpoints: AuthEndpoints;
}

export interface EngineEndpoints {
  schemas: string;
  submit: string;
  validate: string;
}

export interface CacheConfig {
  enabled: boolean;
  duration: number;
}

export interface EngineConfig {
  endpoints: EngineEndpoints;
  cache: CacheConfig;
}

export interface UploadConfig {
  maxSize: number;
  allowedTypes: string[];
  endpoint: string;
}

export interface AnalyticsConfig {
  enabled: boolean;
  trackingId: string;
  endpoint: string;
}

export interface FeatureFlags {
  enablePWA: boolean;
  enableOffline: boolean;
  enablePushNotifications: boolean;
  enableWebShare: boolean;
  enableScreenRecording: boolean;
  // Toggle legacy schema prompts vs centralized warning engine prompts
  useSchemaPrompts?: boolean;
}

export interface MapsConfig {
  provider: string;
  apiKey: string;
}

export interface NotificationsConfig {
  vapidKey: string;
}

export interface ServicesConfig {
  maps: MapsConfig;
  notifications: NotificationsConfig;
}

export interface DevelopmentConfig {
  enableMockAPI: boolean;
  enableConsoleLogging: boolean;
  enablePerformanceMonitoring: boolean;
}

export interface EnvConfig {
  api: APIConfig;
  auth: AuthConfig;
  engine: EngineConfig;
  upload: UploadConfig;
  analytics: AnalyticsConfig;
  features: FeatureFlags;
  services: ServicesConfig;
  development: DevelopmentConfig;
}

/**
 * Get the current environment configuration
 * @returns The current environment configuration
 */
export function getConfig(): EnvConfig {
  return envConfig;
}

export const envConfig: EnvConfig = {
  // API Configuration
  api: {
    baseURL: import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '',
    timeout: 300000,
    retryAttempts: 3,
    retryDelay: 1000
  },
  
  // Authentication
  auth: {
    tokenKey: 'ie_hub_token',
    refreshTokenKey: 'ie_hub_refresh_token',
    tokenExpiry: 3600, // 1 hour
    endpoints: {
      login: '/auth/login',
      logout: '/auth/logout',
      refresh: '/auth/refresh',
      profile: '/auth/profile'
    }
  },
  
  // Engine Configuration
  engine: {
    endpoints: {
      schemas: '/engine/schemas',
      submit: '/engine/submit',
      validate: '/engine/validate'
    },
    cache: {
      enabled: true,
      duration: 300000 // 5 minutes
    }
  },
  
  // File Upload
  upload: {
    maxSize: 10485760, // 10MB
    allowedTypes: ['image/*', 'application/pdf', '.doc', '.docx', '.xls', '.xlsx'],
    endpoint: '/upload'
  },
  
  // Analytics
  analytics: {
    enabled: import.meta.env.VITE_ANALYTICS_ENABLED === 'true',
    trackingId: import.meta.env.VITE_ANALYTICS_TRACKING_ID || '',
    endpoint: '/analytics'
  },
  
  // Feature Flags
  features: {
    enablePWA: true,
    enableOffline: true,
    enablePushNotifications: import.meta.env.VITE_PUSH_NOTIFICATIONS === 'true',
    enableWebShare: true,
    enableScreenRecording: false,
    // Default to true for gradual migration; flip to false after parity
    useSchemaPrompts: (import.meta.env.VITE_USE_SCHEMA_PROMPTS ?? 'true') === 'true'
  },
  
  // External Services
  services: {
    maps: {
      provider: 'google',
      apiKey: PUBLIC_ENV.googleMapsApiKey
    },
    notifications: {
      vapidKey: import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
    }
  },
  
  // Development
  development: {
    enableMockAPI: import.meta.env.VITE_MOCK_API === 'true',
    enableConsoleLogging: import.meta.env.DEV,
    enablePerformanceMonitoring: true
  }
};
